import type { MouseEvent } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Button, LinkButton } from '../../ui';
import { ease, prefersReducedMotion } from '../../lib/motion';
import { useStrings } from '../../state/useLocale';
import type { Nudge } from '../../state/useStuck';

/**
 * The line that appears when the app can see a learner is stuck.
 *
 * One sentence, one offer, one way to wave it away, in the panel where help
 * already lives. Everything it is not is deliberate: not a modal, because a
 * modal takes the terminal away from someone who is mid-thought; not a badge
 * or a bubble, because this is not a notification; not permanent, because an
 * offer that cannot be refused is nagging. It says what it costs — nothing —
 * because a learner who is already unsure will not spend a star to find out.
 *
 * It is `role="status"`, so a screen reader hears it arrive without being
 * dragged away from whatever it was reading.
 *
 * The offer to leave the level is a real `<a href="?mission=…">`, like every
 * other way through the course, so one delegated listener in App turns it
 * into a `pushState` and the Linux in this tab keeps running (browser.md,
 * decision 5). A button would have had to reload the page, which is the one
 * thing a stuck learner should not have to sit through.
 */
export interface UnstuckProps {
  nudge: Nudge | null;
  /** Where "Skip for now" goes: the next level, or the map at the end of the course. */
  skipHref: string;
  /** The learner took the offer: grade the level, or open the next hint. */
  onAccept(): void;
  /** The learner clicked the skip offer. Cancelling the confirmation must `preventDefault`. */
  onSkip(event: MouseEvent<HTMLAnchorElement>): void;
  onDismiss(): void;
}

export function Unstuck({ nudge, skipHref, onAccept, onSkip, onDismiss }: UnstuckProps) {
  // The nudge's own sentence and the label on its offer arrive already in the
  // right language: `decideNudge` built them from the same catalogue. Only
  // the refusal belongs to this component, because it is the same word
  // whatever was offered.
  const s = useStrings().progress.nudge;
  const reduced = prefersReducedMotion();
  return (
    <AnimatePresence initial={false}>
      {nudge && (
        <motion.aside
          key={nudge.id}
          className="nudge"
          role="status"
          initial={reduced ? false : { opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={ease}
        >
          <p className="nudge-text">{nudge.text}</p>
          <p className="nudge-actions">
            {/* The offer of help is the loud one; the offer to leave is not.
                A solid green "Skip for now" would make the exit the most
                attractive thing on the panel, which is the opposite of what
                a learner who could still finish this level needs. */}
            {nudge.action === 'skip' ? (
              <LinkButton variant="quiet" href={skipHref} onClick={onSkip}>
                {nudge.actionLabel}
              </LinkButton>
            ) : (
              <Button variant="solid" onClick={onAccept}>
                {nudge.actionLabel}
              </Button>
            )}
            <Button variant="quiet" onClick={onDismiss}>
              {s.dismiss}
            </Button>
          </p>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
